import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useAuth0 } from "@auth0/auth0-react";
import SFIconButton from "../button/iconButton";
import { SFNotificationIcon, SFTipIcon } from "../icons";
import SFNotificationPanel from "./notificationPanel";
import getStyled from "../../hooks/getStyled";
import { markNotificationsAsRead } from "../../services/notificationService";
import SFBreadcrumbs from "../breadcrumbs";
import SFLanguagePicker from "./languagePicker";
import { AhaIdeaPlugin } from "../aha";
import env from "env";
import { notificationActions } from "./../../redux/reducers/notificationReducer";

const { styled } = getStyled();

const StyledNewNotificationsBadge = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 10,
  right: 11,
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.red400.main,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) =>
    prop !== "disableBorderBottom" && prop !== "hasBoxShadow",
})(({ theme, disableBorderBottom, hasBoxShadow }) => ({
  zIndex: theme.zIndex.drawer - 1,
  width: "100%",
  ...(disableBorderBottom && { borderBottom: 0 }),
  ...(hasBoxShadow && {
    boxShadow: "0px 3px 6px 0px rgba(183, 183, 183, 0.10)",
  }),
}));

const SFAppBar = ({ routes, languages, hideNotifications }) => {
  const { user } = useAuth0();
  const email = user?.email;
  const [tabsRootHasChildren, setTabsRootHasChildren] = useState(false);
  const [autoSaveBarRootHasChildren, setAutoSaveBarRootHasChildren] =
    useState(false);
  const notificationReduxState = useSelector((state) => state.notification);
  //check if action bar is sticky to the header
  const isActionBarSticky = useSelector(
    (state) => state.pageLayout.actionBar.sticky,
  );
  const tabsRootRef = useRef(null);
  const autoSaveBarRootRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const notifications = notificationReduxState.notifications;

  //these useEffects observe the children of the roots
  useEffect(() => {
    // Ensure the ref is current and not null
    if (tabsRootRef.current) {
      // Create a new MutationObserver instance
      const observer = new MutationObserver((mutations) => {
        setTabsRootHasChildren(tabsRootRef.current.childNodes.length > 0);
      });

      // Start observing the target node for configured mutations
      observer.observe(tabsRootRef.current, {
        childList: true, // Set to true to observe changes to the children of the node
        // You can add other configuration options if needed
      });

      // Clean up the observer on component unmount
      return () => {
        observer.disconnect();
      };
    }
  }, []); // Empty dependency array ensures this effect runs only once

  useEffect(() => {
    // Ensure the ref is current and not null
    if (autoSaveBarRootRef.current) {
      // Create a new MutationObserver instance
      const observer = new MutationObserver((mutations) => {
        setAutoSaveBarRootHasChildren(
          autoSaveBarRootRef.current.childNodes?.length > 0,
        );
      });

      // Start observing the target node for configured mutations
      observer.observe(autoSaveBarRootRef.current, {
        childList: true, // Set to true to observe changes to the children of the node
        // You can add other configuration options if needed
      });

      // Clean up the observer on component unmount
      return () => {
        observer.disconnect();
      };
    }
  }, []); // Empty dependency array ensures this effect runs only once

  useEffect(() => {
    if (!hideNotifications) {
      closeNotificationsPanelHandler();
    }
  }, []);

  const openNotificationsPanelHandler = () => {
    //if panel is opening and there are unread messages
    //then mark them as read both in redux and api
    if (
      !notificationReduxState.notificationPanelOpen &&
      notifications.some((x) => !x.read)
    ) {
      markNotificationsAsRead();
      dispatch(notificationActions.markNotificationsAsRead());
    }
    dispatch(notificationActions.setNotificationPanel(true));
  };

  const closeNotificationsPanelHandler = () => {
    dispatch(notificationActions.setNotificationPanel(false));
  };

  return (
    <AppBar
      position="sticky"
      id="appBar"
      //if action bar is sticky to the header and there are no tabs or auto save bar, remove the bottom border
      //this is based on design so the action bar will look like it is part of the header
      disableBorderBottom={
        isActionBarSticky && !tabsRootHasChildren && !autoSaveBarRootHasChildren
      }
      hasBoxShadow={
        tabsRootHasChildren && !isActionBarSticky && !autoSaveBarRootHasChildren
      }
    >
      <Toolbar variant="dense" disableGutters>
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <SFBreadcrumbs routes={routes} />
        </Box>
        {(env.REACT_APP_ENABLE_LANGUAGES ||
          !hideNotifications ||
          env.REACT_APP_AHA_API_KEY) && (
          <>
            <Box sx={{ display: { xs: "flex" } }}>
              {env.REACT_APP_AHA_API_KEY && hideNotifications && ( 
                <AhaIdeaPlugin
                  username={email}
                  apiKey={env.REACT_APP_AHA_API_KEY}
                  projectCode={env.REACT_APP_AHA_PROJECT_CODE}
                  RenderItem={({ onClick }) => (
                    <SFIconButton
                      onClick={onClick}
                      color="black"
                      ariaLabel={t("navigation.submitIdea")}
                    >
                      <SFTipIcon />
                    </SFIconButton>
                  )}
                />
              )}
              {env.REACT_APP_ENABLE_LANGUAGES && !hideNotifications && (
                <SFLanguagePicker languages={languages} />
              )}
              {!hideNotifications && (
                <SFIconButton
                  onClick={openNotificationsPanelHandler}
                  ariaLabel={t(
                    "components.header.notificationPanel.notificationButtonAriaLabel",
                  )}
                >
                  <SFNotificationIcon />
                  {notifications.filter((x) => !x.read).length > 0 && (
                    <StyledNewNotificationsBadge />
                  )}
                </SFIconButton>
              )}
            </Box>
            {notificationReduxState.notificationPanelOpen &&
              !hideNotifications && (
              <SFNotificationPanel
                show={notificationReduxState.notificationPanelOpen}
                onClose={closeNotificationsPanelHandler}
                notifications={notifications}
                onClickNotification={() => {}}
              />
            )}
          </>
        )}
      </Toolbar>
      <div id="tabsLayoutRoot" ref={tabsRootRef} />
      <div id="autoSaveBarRoot" ref={autoSaveBarRootRef} />
    </AppBar>
  );
};

SFAppBar.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(Trans)]),
      path: PropTypes.string,
      icon: PropTypes.element,
    }).isRequired,
  ),
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      language: PropTypes.string.isRequired,
      lang_code: PropTypes.string.isRequired,
    }).isRequired,
  ),
  hideNotifications: PropTypes.bool,
};

export default SFAppBar;
